import { useRuntimeConfig } from '#imports'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import {
  LAPTOP_CATEGORIES,
  MACBOOKS_CATEGORIES,
  ProductCategory,
} from '~/scopes/product-core/constants'

import {
  DEFAULT_MARKETING_VIDEO,
  MARKETING_VIDEOS,
} from '../MarketingVideo.constants'
import type { VideoSource } from '../MarketingVideo.types'
import {
  resolveVideoPoster,
  resolveVideoSources,
} from '../MarketingVideo.utils'

export type GalleryVideoSource = {
  isSelfHosted: boolean
  sources: VideoSource[]
  poster: string
  title: string
}

/**
 * Single source of truth for the PDP gallery video.
 *
 * Decides whether to serve the self-hosted `<video>` (Cloudflare R2) or the
 * legacy YouTube embed, based on the `FF_PRODUCT_PAGE_SELF_HOSTED_VIDEO_ENABLED`
 * feature flag, and resolves the matching sources/poster/title.
 *
 * Every gallery render site and data composable calls this so the
 * YouTube-vs-self-hosted decision (and the poster/title shown in the carousel
 * cell) can't drift between surfaces. Reads only SSR-stable runtime config, so
 * it is hydration-safe and idempotent.
 */
export function useGalleryVideoSource(params?: {
  brand?: string
  categoryId?: string
}): GalleryVideoSource {
  const config = useRuntimeConfig()
  const i18n = useI18n()
  const experiments = useExperiments()

  const isSelfHostedSpecific =
    experiments?.['experiment.selfHostedVideoTypePDP'] === 'selfHostedSpecific'
  const isSelfHosted =
    config.public.FF_PRODUCT_PAGE_SELF_HOSTED_VIDEO_ENABLED === true

  const descriptor = mapToRelevantDescriptor({
    ...params,
    isSelfHosted,
    isSelfHostedSpecific,
  })

  return {
    isSelfHosted,
    sources: isSelfHosted ? resolveVideoSources(descriptor) : [],
    // The poster is the same bundled static image for both paths, so the
    // carousel cell never flashes a different thumbnail when the flag flips.
    poster: resolveVideoPoster(descriptor),
    title: isSelfHosted
      ? i18n(descriptor.title)
      : i18n(DEFAULT_MARKETING_VIDEO.title),
  }
}

function mapToRelevantDescriptor(params: {
  brand?: string
  categoryId?: string
  isSelfHosted: boolean
  isSelfHostedSpecific: boolean
}) {
  if (!params.isSelfHosted) return DEFAULT_MARKETING_VIDEO
  if (!params.isSelfHostedSpecific) return DEFAULT_MARKETING_VIDEO
  if (!params?.brand || !params?.categoryId) return DEFAULT_MARKETING_VIDEO

  const { brand, categoryId } = params

  const isApple = brand.toLowerCase() === 'apple'

  if (MACBOOKS_CATEGORIES.includes(categoryId)) return MARKETING_VIDEOS.MACBOOK
  if (LAPTOP_CATEGORIES.includes(categoryId)) return MARKETING_VIDEOS.LAPTOP
  if (categoryId === ProductCategory.AIRPODS) return MARKETING_VIDEOS.AIRPODS
  if (categoryId === ProductCategory.SMARTPHONE && isApple) {
    return MARKETING_VIDEOS.IPHONE
  }
  if (categoryId === ProductCategory.SMARTPHONE) return MARKETING_VIDEOS.ANDROID

  return DEFAULT_MARKETING_VIDEO
}
